import { Platform, useWindowDimensions } from 'react-native';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import {
  isShellSidebarDestination,
  SHELL_SIDEBAR_CHILD_HUB_ROUTES,
  WEB_SIDEBAR_BREAKPOINT,
} from './navigationManifest';
import { ticketColors } from '../../shared/theme/ticketColors';

/** Presentación de pantallas del ShellStack: card en web ancho; modal opcional en mobile. */
export function shellPresentation(modalOnMobile: boolean, windowWidth: number): 'card' | 'modal' {
  if (Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT) {
    return 'card';
  }
  return modalOnMobile ? 'modal' : 'card';
}

export function isWebSidebarLayout(windowWidth: number): boolean {
  return Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT;
}

/**
 * - `sidebarDestination`: Ventas, Caja, Perfil, Config… — en web no muestran flecha atrás.
 * - `sidebarChild`: hub bajo un destino del sidebar (Integraciones → …) — igual en web; mobile sí ←.
 * - `drillDown`: detalle, datos maestros, etc. — mantienen volver al padre en el stack.
 */
export type ShellScreenBackBehavior = 'sidebarDestination' | 'sidebarChild' | 'drillDown';

/** Flecha atrás solo oculta en web con sidebar fijo (≥ breakpoint); mobile y web angosto la mantienen. */
export function shellHeaderBackVisible(
  windowWidth: number,
  backBehavior: ShellScreenBackBehavior
): boolean {
  if (
    (backBehavior === 'sidebarDestination' || backBehavior === 'sidebarChild') &&
    isWebSidebarLayout(windowWidth)
  ) {
    return false;
  }
  return true;
}

export type ShellScreenOptionsConfig = {
  modalOnMobile?: boolean;
  backBehavior?: ShellScreenBackBehavior;
};

export function buildShellScreenOptions(
  windowWidth: number,
  config: ShellScreenOptionsConfig = {}
): NativeStackNavigationOptions {
  const { modalOnMobile = false } = config;

  return {
    presentation: shellPresentation(modalOnMobile, windowWidth),
    headerShown: true,
    headerShadowVisible: false,
    headerStyle: { backgroundColor: ticketColors.paper },
  };
}

/** Comportamiento de back según nombre de ruta del ShellStack (manifiesto). */
export function shellBackBehaviorForRoute(routeName: string): ShellScreenBackBehavior {
  if (isShellSidebarDestination(routeName)) return 'sidebarDestination';
  if (SHELL_SIDEBAR_CHILD_HUB_ROUTES.has(routeName)) return 'sidebarChild';
  return 'drillDown';
}

export function useShellScreenOptions(config: ShellScreenOptionsConfig = {}) {
  const { width } = useWindowDimensions();
  return buildShellScreenOptions(width, config);
}
